// Imports
import styled, { css } from 'styled-components/macro';
import { GeneratedThemeProps } from '../../templates/template-style';
import PagesMenu from './pages-menu';
import React from 'react';
import { useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import { Outlet, useLocation } from 'react-router-dom';
import { GetHomepageResponse } from '../../../shareables/types/api/main/website/homepage/get';
import UpcomingEvents from './pages/page-types/homepage/upcoming-events';
import HomepageNewsArticles from './pages/page-types/homepage/news-articles';
import Homepage from './pages/page-types/homepage';
import { darken, transparentize } from 'polished';
import YardOfTheMonth from './pages/page-types/homepage/yard-of-the-month';
import RotatingPhotos from './pages/page-types/homepage/rotating-photos';
import GoogleAds from './google-ads';
import ContentAreaError from './pages/content-area-error';
import { BaseErrorBoundary } from '../error-boundary';


// Styled components
const Container = styled.div<{ theme: GeneratedThemeProps }>`
	max-width: 62.5rem;
	margin: 0.625rem auto 1.25rem;
	
	display: grid;
	grid-template-columns: ${(props) => (props.theme.isFlipped ? '1fr 3fr' : '3fr 1fr')};
	
	@media only screen and (max-width: 62rem) {
		grid-template-columns: ${(props) => (props.theme.isFlipped ? '1fr 2fr' : '2fr 1fr')};
	}
	
	@media only screen and (max-width: 47.9375rem) {
		grid-template-columns: 1fr;
	}
	
	${(props) =>
		props.theme.isModernOrNatureStyle &&
		css`
			background: #fff;
			margin-top: 0;
			padding: 0.9375rem 0 1.25rem 1.25rem;
			box-shadow: 0 0 0.625rem rgba(0, 0, 0, 0.3);
			
			grid-template-columns: ${(props) => (props.theme.isFlipped ? '1fr 3fr' : '2.75fr 1fr')};
		`}
`;

export const MainContentWrapper = styled.main<{ theme: GeneratedThemeProps }>`
	padding-right: 0.4688rem;
	max-width: 100%;
	min-width: 0;
	color: #333;
	
	a {
		color: ${(props) => props.theme.contentLinkColor};
		
		&:hover,
		&:focus {
			text-decoration: none;
		}
	}
	
	form {
		button[type='submit'] {
			background-color: ${(props) => props.theme.baseColor};
			
			&:hover,
			&:focus {
				background-color: ${(props) => darken(0.05, props.theme.baseColor)} !important;
			}
			
			&:focus {
				box-shadow: ${(props) => `
					0 0.125em 0.25em rgba(0, 0, 0, 0.05),
					0 0 0 0.125em #fff,
					0 0 0 0.25em ${transparentize(0.75, darken(0.05, props.theme.baseColor))} !important;
				`};
			}
		}
	}
	
	${(props) => {
		const { isFlipped, isModernOrNatureStyle } = props.theme;
		
		if (isFlipped) {
			return css`
				padding-right: 0;
				
				@media only screen and (min-width: 48rem) {
					padding-left: ${isModernOrNatureStyle ? '0.1563rem' : '0.4688rem'};
				}
				${isModernOrNatureStyle && `padding-right: 1.25rem;`}
			`;
		}
		
		if (isModernOrNatureStyle) {
			return css`
				padding-right: 0.375rem;
				
				@media only screen and (max-width: 47.9375rem) {
					padding-right: 1.25rem;
				}
			`;
		}
		
		return css`
			@media only screen and (max-width: 47.9375rem) {
				padding-right: 0;
			}
		`;
	}}
`;

const SideBarWrapper = styled.div<{ theme: GeneratedThemeProps }>`
	margin-top: -0.25rem;
	
	@media only screen and (max-width: 47.9375rem) {
		display: none;
	}
	
	grid-column: ${(props) => (props.theme.isFlipped ? 1 : 2)};
	grid-row: 1;
	
	${({ theme }) => {
		if (theme.isFlipped) {
			return css`
				padding-left: 0;
				padding-right: ${theme.isModernOrNatureStyle ? '1.7188rem' : '1.4063rem'};
			`;
		}
		
		if (theme.isModernOrNatureStyle) {
			return css`
				padding-left: 1.5rem;
				padding-right: 1.25rem;
			`;
		}
		
		return css`
			padding-left: 1.4063rem;
		`;
	}};
`;

export const BottomInfoWrapper = styled.div<{ theme: GeneratedThemeProps }>`
	margin-bottom: 1.25rem;
	
	${(props) =>
		props.theme.isModernOrNatureStyle &&
		css`
			background: #fff;
			max-width: 62.5rem;
			margin-top: -1.25rem;
			margin-left: auto;
			margin-right: auto;
		`}
`;


// Define the accepted props
interface Props {
	storybookHomepage?: GetHomepageResponse;
}

interface OutletErrorBoundaryProps extends React.PropsWithChildren {
	pathname: string;
}


// Error boundary for the content outlet
class OutletErrorBoundary extends BaseErrorBoundary<OutletErrorBoundaryProps> {
	componentDidUpdate(prevProps: OutletErrorBoundaryProps) {
		if (this.state.hasError && prevProps.pathname !== this.props.pathname) {
			this.setState({ hasError: false });
		}
	}
	
	renderError() {
		return <ContentAreaError />;
	}
}


// Function component
const Content: React.FC<Props> = ({ storybookHomepage }) => {
	// Use Redux functionality
	const homepageVisibility = useAppSelector((state) => state.self.homepageVisibility);
	const websitePlan = useAppSelector((state) => state.self.website?.plan);
	const signedIn = useAppSelector((state) => state.common.user.signedIn);
	const memberLoaded = useAppSelector((state) => state.self.memberLoaded);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Container>
				<MainContentWrapper id='main-content'>
					{storybookHomepage && <Homepage storybookHomepage={storybookHomepage} />}
					
					<OutletErrorBoundary pathname={location.pathname}>
						{(!signedIn || memberLoaded) && <Outlet />}
					</OutletErrorBoundary>
					
					{websitePlan === 0 && location.pathname !== '/' && <GoogleAds />}
				</MainContentWrapper>
				
				<SideBarWrapper>
					<PagesMenu />
					
					{location.pathname === '/' && (homepageVisibility?.yotm.description || homepageVisibility?.yotm.photo) && (
						<YardOfTheMonth />
					)}
					
					{location.pathname === '/' && homepageVisibility?.rotatingPhotos && <RotatingPhotos />}
				</SideBarWrapper>
			</Container>
			
			{location.pathname === '/' && (homepageVisibility?.news || homepageVisibility?.events) && (
				<BottomInfoWrapper>
					{homepageVisibility.news && <HomepageNewsArticles />}
					{homepageVisibility.events && <UpcomingEvents />}
				</BottomInfoWrapper>
			)}
		</React.Fragment>
	);
};

export default Content;
